import {createPortal} from 'react-dom';
import {useEffect,useId,useLayoutEffect,useRef,useState} from 'react';
import {Check,FileText,Film,Image,Layers,MessageCircle,X} from 'lucide-react';
import type {TabState} from '../workspace-context';
import {ToolbarMenu} from './Pane';

export interface TabItem {id:string;title:string;kind?:string}

export function TabOverflow({items,selected,onSelect,onClose}:{items:TabItem[];selected:string[];onSelect:(id:string)=>void;onClose:(id:string)=>void}){
  return <ToolbarMenu label="所有标签页" icon={<Layers size={18}/>}>
    {items.length?items.map(item=><div className="overflow-tab-row" key={item.id}><button data-menu-action onClick={()=>onSelect(item.id)}><span>{item.title}</span>{selected.includes(item.id)&&<Check size={14}/>}</button><button className="icon-button" aria-label={`关闭标签 ${item.title}`} onClick={()=>onClose(item.id)}><X size={13}/></button></div>):<span className="menu-empty">没有打开的标签页</span>}
  </ToolbarMenu>;
}

/** ReadOSTabPresentationUI: a 42px strip containing 38px capsule tabs. */
export function TabStrip({state,items,label,onSelect,onClose}:{
  state:TabState;
  items:TabItem[];
  label:string;
  onSelect:(id:string)=>void;
  onClose:(id:string)=>void;
}){
  const strip=useRef<HTMLDivElement>(null);
  const tooltipRef=useRef<HTMLDivElement>(null);
  const tooltipAnchor=useRef<HTMLElement|null>(null);
  const tooltipId=`reados-tab-tooltip-${useId().replace(/:/g,'')}`;
  const [tooltip,setTooltip]=useState<{itemId:string;title:string;left:number;top:number}|null>(null);
  const repositionTooltip=()=>{
    const anchor=tooltipAnchor.current;if(!anchor)return;
    const anchorRect=anchor.getBoundingClientRect();
    const tooltipWidth=tooltipRef.current?.offsetWidth||Math.min(420,window.innerWidth-16);
    const tooltipHeight=tooltipRef.current?.offsetHeight||64;
    const left=Math.max(8,Math.min(anchorRect.left,window.innerWidth-tooltipWidth-8));
    const below=anchorRect.bottom+8;
    const top=below+tooltipHeight<=window.innerHeight-8?below:Math.max(8,anchorRect.top-tooltipHeight-8);
    setTooltip(current=>current&&(current.left!==left||current.top!==top)?{...current,left,top}:current);
  };
  const showTooltip=(item:TabItem,anchor:HTMLElement)=>{
    tooltipAnchor.current=anchor;
    const rect=anchor.getBoundingClientRect();
    setTooltip({itemId:item.id,title:item.title,left:rect.left,top:rect.bottom+8});
  };
  const hideTooltip=(itemId?:string)=>setTooltip(current=>!itemId||current?.itemId===itemId?null:current);
  useLayoutEffect(()=>{if(tooltip)repositionTooltip();},[tooltip?.itemId,tooltip?.title]);
  useEffect(()=>{
    if(!tooltip)return;
    const dismiss=()=>hideTooltip();
    const escape=(event:KeyboardEvent)=>{if(event.key==='Escape')dismiss();};
    window.addEventListener('resize',dismiss);
    window.addEventListener('scroll',dismiss,true);
    window.addEventListener('keydown',escape);
    return()=>{window.removeEventListener('resize',dismiss);window.removeEventListener('scroll',dismiss,true);window.removeEventListener('keydown',escape);};
  },[tooltip?.itemId]);
  useEffect(()=>{
    if(tooltip&&!items.some(item=>item.id===tooltip.itemId))hideTooltip();
  },[items,tooltip]);
  useEffect(()=>{
    const selected=strip.current?.querySelector<HTMLElement>('[data-focused="true"]');
    selected?.scrollIntoView({block:'nearest',inline:'nearest'});
  },[state.active]);
  useEffect(()=>{
    const element=strip.current;if(!element)return;
    const scrollWithWheel=(event:WheelEvent)=>{
      // Leave horizontal trackpad gestures and browser zoom to the browser.
      if(event.defaultPrevented||event.ctrlKey||event.metaKey||event.deltaX!==0||event.deltaY===0)return;
      const maximum=element.scrollWidth-element.clientWidth;
      if(maximum<=0)return;
      const unit=event.deltaMode===WheelEvent.DOM_DELTA_LINE?16:event.deltaMode===WheelEvent.DOM_DELTA_PAGE?element.clientWidth:1;
      const next=Math.max(0,Math.min(maximum,element.scrollLeft+event.deltaY*unit));
      if(next===element.scrollLeft)return;
      // React wheel handlers are passive; cancellation here avoids scrolling
      // the surrounding workspace while moving the tab strip.
      event.preventDefault();element.scrollLeft=next;
    };
    element.addEventListener('wheel',scrollWithWheel,{passive:false});
    return()=>element.removeEventListener('wheel',scrollWithWheel);
  },[]);
  return <div ref={strip} className="tab-scroll" role="tablist" aria-label={`${label}标签页`} aria-description="可用鼠标滚轮左右浏览标签页">
    {items.map((item,index)=>{
      const selected=item.id===state.active||(state.split&&item.id===state.secondary);
      const Icon=item.kind==='chat'?MessageCircle:item.kind==='video'?Film:item.kind==='image'?Image:FileText;
      return <div className={`workspace-tab ${selected?'selected':''}`} data-focused={item.id===state.active} key={item.id}
        onPointerEnter={event=>{if(event.pointerType!=='touch')showTooltip(item,event.currentTarget)}}
        onPointerLeave={event=>{if(!event.currentTarget.querySelector(':focus-visible'))hideTooltip(item.id)}}
        onFocusCapture={event=>{if(event.target.matches(':focus-visible'))showTooltip(item,event.currentTarget)}}
        onBlurCapture={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node|null)&&!event.currentTarget.matches(':hover'))hideTooltip(item.id)}}>
        <button role="tab" aria-selected={selected} aria-label={item.title} aria-describedby={tooltip?.itemId===item.id?tooltipId:undefined} onClick={()=>{hideTooltip();onSelect(item.id)}} onKeyDown={event=>{
          if(event.key==='ArrowRight'||event.key==='ArrowLeft'){
            event.preventDefault();
            onSelect(items[(index+(event.key==='ArrowRight'?1:items.length-1))%items.length].id);
          }
          if(event.key==='Delete')onClose(item.id);
        }}><Icon size={18} strokeWidth={1.6}/><span>{item.title}</span></button>
        <button className="tab-close" aria-label={`关闭标签 ${item.title}`} onClick={()=>{hideTooltip();onClose(item.id)}}><X size={12}/></button>
      </div>;
    })}
    {tooltip&&typeof document!=='undefined'&&createPortal(<div ref={tooltipRef} id={tooltipId} role="tooltip" className="workspace-tab-tooltip" style={{left:tooltip.left,top:tooltip.top}}>{tooltip.title}</div>,document.body)}
  </div>;
}
